import { BoardStroke } from "../../domain/whiteboard";

/**
 * Paint a whole board onto a 2D context, leaving the background alone.
 *
 * The background stays untouched because erasing is done by cutting through
 * the strokes; painting white underneath first would mean the eraser cut
 * through that too and left a hole. The live canvas gets its white from CSS,
 * and a saved snapshot is composed onto white in a second pass.
 */
export function drawBoard(
    ctx: CanvasRenderingContext2D,
    strokes: BoardStroke[],
    width: number,
    height: number
): void {
    ctx.clearRect(0, 0, width, height);

    for (const stroke of strokes) {
        drawStroke(ctx, stroke, width, height);
    }
}

export function drawStroke(
    ctx: CanvasRenderingContext2D,
    stroke: BoardStroke,
    width: number,
    height: number
): void {
    if (stroke.points.length === 0) return;

    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(1, stroke.width * width);
    // Erasing cuts through what is already there instead of painting over it,
    // so a board saved on a white background has no grey smudges on it.
    ctx.globalCompositeOperation = stroke.erase ? "destination-out" : "source-over";
    ctx.strokeStyle = stroke.erase ? "rgba(0,0,0,1)" : stroke.color;

    ctx.beginPath();
    const [first, ...rest] = stroke.points;
    ctx.moveTo(first.x * width, first.y * height);

    if (rest.length === 0) {
        // A tap is a dot, not nothing.
        ctx.lineTo(first.x * width + 0.01, first.y * height);
    }
    for (const point of rest) {
        ctx.lineTo(point.x * width, point.y * height);
    }

    ctx.stroke();
    ctx.restore();
}

/**
 * Paint only the newest segment of a stroke still under the pointer.
 *
 * Repainting the whole stroke on every pointer move costs more with every
 * point already in it, so a long unbroken line would get slower the longer it
 * gets — exactly when the person drawing it is least willing to wait.
 */
export function drawLastSegment(
    ctx: CanvasRenderingContext2D,
    stroke: BoardStroke,
    width: number,
    height: number
): void {
    if (stroke.points.length < 2) {
        drawStroke(ctx, stroke, width, height);
        return;
    }

    const from = stroke.points[stroke.points.length - 2];
    const to = stroke.points[stroke.points.length - 1];

    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(1, stroke.width * width);
    ctx.globalCompositeOperation = stroke.erase ? "destination-out" : "source-over";
    ctx.strokeStyle = stroke.erase ? "rgba(0,0,0,1)" : stroke.color;

    ctx.beginPath();
    ctx.moveTo(from.x * width, from.y * height);
    ctx.lineTo(to.x * width, to.y * height);
    ctx.stroke();
    ctx.restore();
}
